import React, { useState } from "react";
import { X, UserPlus, Copy, ExternalLink } from "react-feather";
import agentActivityService from "../../../services/agentActivityService";
import { success, error as notifyError } from "../../../utils/notification";

interface IInviteSupplierModalProps {
    isOpen: boolean;
    onClose: () => void;
    activityId: string;
    hubspotId: string;
    supplierEmail: string;
    supplierName: string;
    onSuccess: (updatedActivity: any) => void;
}

const InviteSupplierModal: React.FC<IInviteSupplierModalProps> = ({
    isOpen,
    onClose,
    activityId,
    hubspotId,
    supplierEmail,
    supplierName,
    onSuccess
}) => {
    const [loading, setLoading] = useState(false);
    const [inviteUrl, setInviteUrl] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleInvite = async () => {
        setLoading(true);
        try {
            const response = await agentActivityService.inviteSupplier({
                activity_id: activityId,
                hubspot_id: hubspotId,
                supplier_email: supplierEmail,
                supplier_name: supplierName
            });
            success("Supplier invited successfully!");
            if (response.data.invite_url) {
                setInviteUrl(response.data.invite_url);
            }
            onSuccess(response.data);
        } catch (err) {
            const error = err as any;
            console.error("Failed to invite supplier:", error);
            notifyError(error.response?.data?.message || "Failed to invite supplier. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        if (inviteUrl) {
            navigator.clipboard.writeText(inviteUrl);
            success("Invite URL copied to clipboard!");
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content invite-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Invite to Platform</h2>
                    <button className="close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="modal-body">
                    {!inviteUrl ? (
                        <div className="invite-confirmation">
                            <p>Are you sure you want to invite <strong>{supplierName}</strong> to the Journey Foods platform?</p>
                            <p className="sub-text">An invitation will be sent to <strong>{supplierEmail}</strong>.</p>
                        </div>
                    ) : (
                        <div className="invite-success-view">
                            <div className="success-icon">
                                <UserPlus size={48} className="text-green" />
                            </div>
                            <h3>Invitation Sent!</h3>
                            <p>You can also share this direct invite link:</p>
                            <div className="url-copy-box">
                                <input type="text" readOnly value={inviteUrl} />
                                <button onClick={copyToClipboard} title="Copy to clipboard">
                                    <Copy size={16} />
                                </button>
                            </div>
                            <div className="invite-actions" style={{ marginTop: 24 }}>
                                <a href={inviteUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                                    <ExternalLink size={16} />
                                    Open Invite Link
                                </a>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        {inviteUrl ? "Close" : "Cancel"}
                    </button>
                    {!inviteUrl && (
                        <button type="button" className="btn btn-primary" onClick={handleInvite} disabled={loading}>
                            {loading ? "Inviting..." : "Send Invitation"}
                            {!loading && <UserPlus size={16} style={{ marginLeft: 8 }} />}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InviteSupplierModal;
